import React from "react";
import { ExternalLink, MonitorPlay } from "lucide-react";
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "lift-ui";

interface Props {
  url: string | null;
  appName?: string | null;
}

// Brand path used in the sandbox URL. Hard-coded for now (matches the
// preview link pattern requested).
const SANDBOX_BRAND = "LIFT Demo Brand";
const LIFT_BASE_URL =
  import.meta.env.VITE_LIFT_URL?.replace(/\/+$/, "") ??
  "https://lift.newengen.com";
const SANDBOX_BASE = `${LIFT_BASE_URL}/performance-suite/claude-app-sandbox/claude-preview`;

// kebab-case → PascalCase ("crm-weekly-report" → "CrmWeeklyReport").
const toPascalCase = (s: string): string =>
  s
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join("");

const buildSandboxUrl = (
  previewUrl: string,
  appName: string | null | undefined
): string => {
  const trimmed = previewUrl.replace(/\/+$/, "");
  const remoteEntry = `${trimmed}/remoteEntry.js`;
  const brand = encodeURIComponent(SANDBOX_BRAND);
  const params = new URLSearchParams({
    claudeApp: remoteEntry,
    appName: appName ? toPascalCase(appName) : "ClaudeApp",
  });
  return `${SANDBOX_BASE}/${brand}/${brand}?${params.toString()}`;
};

const AppPreviewLink: React.FC<Props> = ({ url, appName }) => {
  if (!url) {
    return <span className="text-gray-400 text-xs">—</span>;
  }
  const sandboxUrl = buildSandboxUrl(url, appName);
  return (
    <div className="flex items-center justify-center gap-1">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                window.open(sandboxUrl, "_blank", "noopener,noreferrer");
              }}
              className="h-8 w-8 text-[#334238] hover:bg-[#dbf566]/20"
            >
              <MonitorPlay className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Open in LIFT sandbox</p>
            <p className="max-w-[400px] break-all text-[11px] text-gray-400 mt-1">
              {sandboxUrl}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default AppPreviewLink;
